import * as React from 'react';
import type { NativeMethods } from 'react-native';

import { PortalContext } from './context';
import { PortalRelative, type PortalRelativeProps, type PortalRelativeRef } from './PortalRelative';
import type { CloseableCauses } from './useCloseable';

export interface PortalToggleableRef {
    open: () => void;
    close: () => void;
    toggle: () => void;
}

export type PortalToggleableProps<T extends NativeMethods> = PortalRelativeProps<T> & {
    anchor: (props: {
        ref: React.Ref<T>;
        opened: boolean;
        open: () => void;
        close: () => void;
        toggle: () => void;
    }) => React.ReactNode;
    onClose?: (cause?: CloseableCauses) => void;
    onOpen?: () => void;
    children: React.ReactNode;
};

/**
 * Easy to use portal for toggleable menu/popover.
 */
const PortalToggleable = React.forwardRef(function Portal<T extends NativeMethods>(
    props: PortalToggleableProps<T>,
    inputRef: React.Ref<PortalToggleableRef>
): React.ReactElement {
    const { children, anchor, ...rest } = props;
    const [opened, setOpened] = React.useState(false);
    const portalRef = React.useRef<PortalRelativeRef>(null);

    const open = () => {
        setOpened(true);
        if (props.onOpen) {
            props.onOpen();
        }
    };

    const close = () => {
        setOpened(false);
        if (props.onClose) {
            props.onClose({});
        }
    };

    const toggle = () => {
        if (opened) {
            close();
        } else {
            open();
        }
    };

    React.useImperativeHandle(
        inputRef,
        (): PortalToggleableRef => ({
            open,
            close,
            toggle,
        })
    );

    return (
        <PortalContext.Provider value={close}>
            <PortalRelative
                {...rest}
                ref={portalRef}
                opened={opened}
                anchor={({ ref }) =>
                    anchor({
                        ref,
                        opened,
                        toggle,
                        open,
                        close,
                    })
                }
            >
                {children}
            </PortalRelative>
        </PortalContext.Provider>
    );
}) as <T extends NativeMethods>(
    p: PortalToggleableProps<T> & { ref?: React.Ref<PortalToggleableRef> }
) => React.ReactElement;

// @ts-ignore
PortalToggleable.displayName = 'PortalToggleable';

export { PortalToggleable };
